import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import usePageMeta from "@/hooks/usePageMeta";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9l2dhrh5tg6sock770";

const faqs = [
  {
    question: "可以在站内观看影片吗？",
    answer:
      "不可以。伦理视频是内容介绍与影评站点，不提供视频播放、下载或观看链接。所有作品信息仅用于剧情与人物分析。",
  },
  {
    question: "如何向我们推荐一部作品？",
    answer:
      "通过本页表单或邮箱留言，写清片名、年份与推荐理由即可。我们更关注作品是否真正处理了伦理困境，而不是题材标签。",
  },
  {
    question: "表单提交后会保存吗？",
    answer:
      "本页表单用于收集留言反馈，提交后会有成功提示。演示阶段的数据仅用于测试提交流程，不作为账号或用户系统使用。",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [messageLength, setMessageLength] = useState(0);

  usePageMeta(
    "联系我们 - 伦理视频",
    "联系伦理视频编辑部：作品推荐、内容纠错、合作咨询都欢迎来信。可通过页面邮箱或留言表单与我们取得联系。",
    "联系伦理视频,影视网站联系方式,作品推荐,内容纠错,伦理视频留言",
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    formData.delete("company_alt");

    const name = String(formData.get("name") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写姓名、邮箱与留言内容后再提交。");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("邮箱格式看起来不太对，请检查后再提交。");
      return;
    }

    if (message.length > 500) {
      setStatus("error");
      setFormError("留言内容超过 500 字，请精简后再提交。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const normalised = String(serverMessage || "").toLowerCase();
      const isSpam = normalised.includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          isSpam
            ? "提交被判定为异常内容，请修改留言后重试。"
            : String(serverMessage || "提交失败，请稍后再试。"),
        );
        return;
      }

      setStatus("success");
      setMessageLength(0);
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络连接异常，提交没有完成。请检查网络后重试。");
    }
  };

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Contact"
        title="联系我们"
        description="作品推荐、内容纠错、转载与合作，都欢迎来信。我们通常在两个工作日内回复，写得越具体，回复越快。"
        breadcrumbs={[{ label: "首页", to: "/" }, { label: "联系我们" }]}
      />

      <section className="w-full px-4 py-10 md:px-10 md:py-14">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-5">
            <div className="rounded-lg bg-primary-900 p-6 md:p-7">
              <h2 className="font-heading text-lg font-semibold text-background-50 md:text-xl">直接写信给我们</h2>
              <p className="mt-2 text-xs leading-relaxed text-background-300">
                如果不方便填表，也可以直接发邮件。请在标题里写明来意，比如「推荐作品」或「内容纠错」。
              </p>

              <a
                href="mailto:contact@lunli-video.com"
                className="mt-5 flex items-center gap-3 rounded-md bg-background-50/10 px-4 py-3 ring-1 ring-background-50/15 transition-colors hover:bg-background-50/15 cursor-pointer"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-mail-line" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] text-background-400">反馈邮箱</span>
                  <span className="block truncate text-sm text-background-50">contact@lunli-video.com</span>
                </span>
              </a>

              <div className="mt-5 space-y-3 border-t border-background-50/10 pt-5">
                <div className="flex items-start gap-3">
                  <i className="ri-time-line mt-0.5 text-accent-400" />
                  <div>
                    <p className="text-xs font-medium text-background-100">回复时间</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-background-400">
                      周一至周五 10:00 - 18:00，节假日顺延。
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-lightbulb-line mt-0.5 text-accent-400" />
                  <div>
                    <p className="text-xs font-medium text-background-100">推荐作品时请提供</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-background-400">
                      片名、年份、地区，以及你认为它值得讨论的那个伦理问题。
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-shield-check-line mt-0.5 text-accent-400" />
                  <div>
                    <p className="text-xs font-medium text-background-100">关于你的信息</p>
                    <p className="mt-0.5 text-[11px] leading-relaxed text-background-400">
                      我们只用它回复本次留言，不会用于订阅或其它用途。
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="font-heading text-base font-semibold text-foreground-950">常见问题</h2>
              <div className="mt-3 divide-y divide-background-200">
                {faqs.map((faq, index) => {
                  const open = openFaq === index;
                  return (
                    <div key={faq.question} className="py-3">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? null : index)}
                        className="flex w-full items-center justify-between gap-3 text-left cursor-pointer"
                      >
                        <span className="text-xs font-medium text-foreground-900 md:text-sm">{faq.question}</span>
                        <i
                          className={`shrink-0 text-foreground-500 ${
                            open ? "ri-subtract-line" : "ri-add-line"
                          }`}
                        />
                      </button>
                      {open && (
                        <p className="mt-2 text-xs leading-relaxed text-foreground-600">{faq.answer}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
              <h2 className="font-heading text-lg font-semibold text-foreground-950 md:text-xl">留言表单</h2>
              <p className="mt-1.5 text-xs text-foreground-500">
                带 <span className="text-accent-600">*</span> 的为必填项，留言请控制在 500 字以内。
              </p>

              {status === "success" ? (
                <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 px-6 py-12 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-500 text-background-50">
                    <i className="ri-check-line text-2xl" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">留言已提交</h3>
                  <p className="mt-2 max-w-sm text-sm leading-relaxed text-foreground-600">
                    感谢你的来信。我们会在两个工作日内查看，并通过你留下的邮箱回复。
                  </p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-5 flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
                  >
                    <i className="ri-edit-line" />
                    再写一条
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  action={SUBMIT_ADDR}
                  method="post"
                  data-readdy-form
                  className="mt-5 space-y-4"
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-1.5 block text-xs text-foreground-700">
                        姓名 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        maxLength={40}
                        placeholder="怎么称呼你"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-1.5 block text-xs text-foreground-700">
                        邮箱 <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        maxLength={80}
                        placeholder="name@example.com"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-topic" className="mb-1.5 block text-xs text-foreground-700">
                      留言主题
                    </label>
                    <div className="relative">
                      <select
                        id="contact-topic"
                        name="topic"
                        defaultValue="推荐作品"
                        className="w-full appearance-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 pr-9 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 cursor-pointer"
                      >
                        <option value="推荐作品">推荐作品</option>
                        <option value="内容纠错">内容纠错</option>
                        <option value="转载与合作">转载与合作</option>
                        <option value="其它反馈">其它反馈</option>
                      </select>
                      <i className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-foreground-500" />
                    </div>
                  </div>

                  <div>
                    <div className="mb-1.5 flex items-center justify-between">
                      <label htmlFor="contact-message" className="block text-xs text-foreground-700">
                        留言内容 <span className="text-accent-600">*</span>
                      </label>
                      <span className="text-[11px] text-foreground-400">{messageLength} / 500</span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      onChange={(event) => setMessageLength(event.target.value.length)}
                      placeholder="想推荐的作品、发现的错误，或者任何想说的话……"
                      className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-assist-note"
                  />

                  {status === "error" && formError && (
                    <div className="flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-3 py-2.5">
                      <i className="ri-error-warning-line mt-0.5 text-accent-700" />
                      <p className="text-xs leading-relaxed text-accent-900">{formError}</p>
                    </div>
                  )}

                  <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-[11px] leading-relaxed text-foreground-400">
                      提交即表示你同意我们使用邮箱回复本次留言。
                    </p>
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 cursor-pointer"
                    >
                      {status === "submitting" ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" />
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line" />
                          提交留言
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <p className="mt-4 rounded-md bg-background-100 px-4 py-3 text-[11px] leading-relaxed text-foreground-500">
              说明：本表单用于演示留言与提交状态反馈流程，数据由平台表单服务接收，未与任何用户账号系统关联。站内不提供评论区与用户互动功能。
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}